import React, { useEffect, useState } from "react";
import { Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Button, TextField, } from "@mui/material";
import DatePicker from "../common/inputs/DatePicker/DatePicker";
import { adjustBookingTypes } from "../../views/booking-overview/SupplierBooking";
import {DateFormats} from "../constants/securspace-constants";
import { adjustEndDateRecurring, adjustNumberOfSpaces, adjustEndDate } from "../../views/requests/booking-requests";
import {getErrorMessage} from "../../util/NetworkErrorUtil";
import { getRecurringProperties } from "../../util/BookingUtil";
import moment from "moment";
import {DAILY, MONTHLY, RECURRING} from "../../util/BookingUtil";
import {formatCurrencyValue} from "../../util/PaymentUtils";
import SelectInput from "../SelectInput";
import {useSnackbar} from "../../context/snackbar-context";

const AdjustBookingSpaceDialogContent = ({booking, handleAdjustSpaces, handleAdjustBooking}) => {
    let payoutPerText;
    switch (booking?.durationType) {
        case DAILY:
            payoutPerText = 'Per Day';
            break;
        case MONTHLY:
            payoutPerText = 'Per Month';
            break;
        default:
            payoutPerText = 'Per Occurrence';
    }

    const shouldDisplayBrokeredBookingFields = booking && booking.brokered
      && booking.brokeredSupplierPaidPerOccurrence !== undefined && booking.brokeredSupplierPaidPerOccurrence !== null
      && booking.brokeredBuyerChargedPerOccurrence !== undefined && booking.brokeredBuyerChargedPerOccurrence !== null;

    let errorMessage = '';
    if (shouldDisplayBrokeredBookingFields && booking.brokeredSupplierPaidPerOccurrence > booking.brokeredBuyerChargedPerOccurrence) {
        errorMessage = 'Supplier payout amount is greater than buyer charged amount.';
    }

    return <>
        <FormControl variant={'standard'} fullWidth>
            <TextField type="number" placeholder="Enter the new number of spaces" value={booking?.numberOfSpaces} onChange={handleAdjustSpaces}/>
        </FormControl>
        {
          shouldDisplayBrokeredBookingFields && <>
                <TextField
                  name='brokeredSupplierPaidPerOccurrence'
                  value={formatCurrencyValue(booking.brokeredSupplierPaidPerOccurrence, true)}
                  label={`New Supplier Payout ${payoutPerText}`}
                  helperText={errorMessage || `Enter the new supplier payout ${payoutPerText.toLowerCase()}`}
                  placeholder='e.g. $50.00'
                  onChange={handleAdjustBooking}
                  fullWidth
                  required
                  variant='standard'
                  className={'mt-2'}
                  error={!!errorMessage}
                />
                <TextField
                  name='brokeredBuyerChargedPerOccurrence'
                  value={formatCurrencyValue(booking.brokeredBuyerChargedPerOccurrence, true)}
                  label={`New Buyer Charged ${payoutPerText}`}
                  helperText={`Enter the new buyer charge ${payoutPerText.toLowerCase()}`}
                  placeholder='e.g. $50.00'
                  onChange={handleAdjustBooking}
                  fullWidth
                  required
                  variant='standard'
                />
            </>
        }
    </>
};


const AdjustBookingDialog = ({open, onClose, onConfirm, type, booking, handleAdjustEndDate, handleAdjustSpaces, handleAdjustBooking}) => {

    const [recurringDateSelectOptions, setRecurringDateSelectOptions] = useState([]);
    const [selectedNewEndDate, setSelectedNewEndDate] = useState();
    const { snackbarShowMessage } = useSnackbar();

  const submitUpdateSpaces = () => {

        let requestBody = {};

        if(booking) {
            const {numberOfSpaces, brokeredSupplierPaidPerOccurrence, brokeredBuyerChargedPerOccurrence, id} = booking;

            requestBody.numberOfSpaces = numberOfSpaces;
            requestBody.bookingId = id;
            requestBody.brokeredSupplierPaidPerOccurrence = brokeredSupplierPaidPerOccurrence;
            requestBody.brokeredBuyerChargedPerOccurrence = brokeredBuyerChargedPerOccurrence;
            if (booking.brokered && booking.brokeredSupplierPaidPerOccurrence > booking.brokeredBuyerChargedPerOccurrence) {
                snackbarShowMessage('Supplier payout amount cannot be greater than buyer charged amount.', 'error');
                return;
            }
        }

        adjustNumberOfSpaces(requestBody)
            .then(({body}) => {
                onConfirm();
                snackbarShowMessage(`Successfully updated number of spaces for booking ${body.orderNumber}`, 'success');
            }).catch(err => snackbarShowMessage(getErrorMessage(err), 'error'))
            .finally(() => onClose());

    }

    const submitUpdateDate = () => {

        if(booking) {
            let requestBody = {bookingId: booking.id};

            if(booking.frequency === RECURRING) {
                const {value} = selectedNewEndDate;
                requestBody.endDate = moment(value).format('M/DD/YYYY');

                adjustEndDateRecurring(requestBody)
                .then(({body}) => {
                    onConfirm();
                    snackbarShowMessage(`Successfully updated end date for booking ${body.orderNumber}`, 'success');
                }).catch(err => snackbarShowMessage(getErrorMessage(err), 'error'))
                .finally(() => onClose());

            } else {
                requestBody.endDate = moment(booking.endDate).format(DateFormats.DAY);

                adjustEndDate(requestBody)
                .then(({body}) => {
                    onConfirm();
                    snackbarShowMessage(`Successfully updated end date for booking ${body.orderNumber}`, 'success');
                }).catch(err => snackbarShowMessage(getErrorMessage(err), 'error'))
                .finally(() => onClose());
            }
        }

    }

    const handleSubmit = () => {
        if(type === adjustBookingTypes.DATE) {
            submitUpdateDate()
        } else {
            submitUpdateSpaces()
        }
    };

    const updateSpaces = (event) => {
        if (event.target && type === adjustBookingTypes.SPACES) {
            const {value} = event.target;
            handleAdjustSpaces(value)
        }
    }

    const handleSelectNewEndDateRecurringBooking = (event) => {
        const {value} = event.target;

        setSelectedNewEndDate(recurringDateSelectOptions.find(item => item.value === value));
    }

    useEffect(() => {
        if(booking?.frequency === RECURRING) {
            setRecurringDateSelectOptions(getRecurringProperties(booking.durationType, booking.startDate));
        }
    }, [booking]);

    useEffect(() => {
        const dateMatch = recurringDateSelectOptions.find(option => {
            const endDate = moment(booking?.endDate)
            return moment(option.value).isSame(endDate);
        })
        setSelectedNewEndDate(dateMatch)
    }, [booking, recurringDateSelectOptions]);


    return (
        <Dialog open={open}>
            <DialogTitle>Adjust {type}</DialogTitle>
            <DialogContent>
                {
                    type === adjustBookingTypes.DATE ?
                        <FormControl variant={'standard'} fullWidth>
                            {
                                booking?.frequency === RECURRING ?
                                    <SelectInput
                                        label="End Date"
                                        value={selectedNewEndDate?.value}
                                        onChange={handleSelectNewEndDateRecurringBooking}
                                        selectData={recurringDateSelectOptions}
                                        hasAction={false}
                                    />
                                :
                                    <DatePicker
                                        label="End Date"
                                        value={booking?.endDate}
                                        onChange={(date) => {
                                          if (!date) return;
                                          handleAdjustEndDate(moment(date).format(DateFormats.DAY));
                                        }}
                                        dateFormat={DateFormats.DAY}
                                    />
                            }
                        </FormControl>
                    :
                      <AdjustBookingSpaceDialogContent
                        handleAdjustSpaces={updateSpaces}
                        booking={booking}
                        handleAdjustBooking={handleAdjustBooking}
                      />
                }
            </DialogContent>
            <DialogActions>
                <Button color='primary' onClick={onClose}>Cancel</Button>
                <Button color='primary' onClick={handleSubmit}>Confirm</Button>
            </DialogActions>
        </Dialog>
    )
}

export default AdjustBookingDialog;
